import { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import DefaultFormCAreaParams from 'src/components/ui/DefaultForm/connectors/AreaParams';
import RightDrawer from 'src/components/ui/RightDrawer';
import DefaultFormCConnectedAreas from 'src/components/ui/DefaultForm/connectors/ConnectedAreas';
import { ACTION_CODE } from 'src/components/ui/IconActions/constants';
import DefaultFormCAddObjection from 'src/components/ui/DefaultForm/connectors/AddObjection';
import DefaultFormCRemoveObjection from 'src/components/ui/DefaultForm/connectors/RemoveObjection';
import DefaultFormCCommonNumber from 'src/components/ui/DefaultForm/connectors/CommonNumber';
import DefaultFormCPersonNumber from 'src/components/ui/DefaultForm/connectors/PersonNumber';
import DefaultFormCRemoveArea from 'src/components/ui/DefaultForm/connectors/RemoveArea';
import DefaultFormCDocUpload from 'src/components/ui/DefaultForm/connectors/DocUpload';
import { CallsPickerForm } from 'src/components/ui/CallsPicker/stories/Form';
import DefaultFormCWorth from 'src/components/ui/DefaultForm/connectors/Worth';
import {
	buildWorthFormOptions,
	mapWorthToFormValues,
} from 'src/utils/mappers/worth/form.mapper';
import { mapEventSelectionsToTableValuation } from 'src/utils/mappers/worth/eventSelections.mapper';
import TableValuation from 'src/components/ui/TableValuation';
import DefaultFormCMeeting from 'src/components/ui/DefaultForm/connectors/Meeting';
import DefaultFormCMessaging from 'src/components/ui/DefaultForm/connectors/Messaging';
import DefaultFormCAreaName from 'src/components/ui/DefaultForm/connectors/AreaName';

import EventForm from '../EventForm';
import { useEventFormGroups } from 'src/hooks/useEventFormGroups';
import DefaultFormCChangeContact from '../../DefaultForm/connectors/ChangeContact';
import { createActivity } from 'src/services/createActivity';
import { updateActivity } from 'src/services/updateActivity';
import { mapActivityPayload } from 'src/utils/mappers/eventForm/activityPayload.mapper';
import { mapContactChangePreview } from 'src/utils/mappers/eventForm/contact.mapper';
import {
	COMPANY_STATUS_CODE,
	NO_CHANGE_STATUS_OPTION,
	STATUS_REASON_OPTIONS,
} from 'src/utils/mappers/companyStatus.mapper';
import dayjs from 'dayjs';

const MODAL_ACTION_MAP = {
	[ACTION_CODE.addObjection]: { type: 'objection', action: 'create' },
	[ACTION_CODE.removeObjection]: { type: 'objection', action: 'remove' },
	[ACTION_CODE.addPersonalContact]: { type: 'contact', action: 'create' },
	[ACTION_CODE.addCommonNumber]: { type: 'number', action: 'create' },
	[ACTION_CODE.editContact]: { type: 'contact', action: 'change' },
	[ACTION_CODE.addContract]: { type: 'contract', action: 'create' },
	[ACTION_CODE.addDoc]: { type: 'doc', action: 'create' },
	[ACTION_CODE.params]: { type: 'params', action: 'change' },
	[ACTION_CODE.meeting]: { type: 'meeting', action: 'create' },
	[ACTION_CODE.messaging]: { type: 'messaging', action: 'create' },
	[ACTION_CODE.worth]: { type: 'worth', action: 'change' },
	[ACTION_CODE.connected]: { type: 'companyGroup', action: 'link' },
	[ACTION_CODE.addArea]: { type: 'companyGroup', action: 'link' },
	[ACTION_CODE.removeArea]: { type: 'companyGroup', action: 'unlink' },
};

const EventFormCWithModals = (props) => {
	const {
		defaultAction = null,
		comment: commentProps = {},
		status: statusProps = {},
		actions = {},
		submitLabel = '',
		cancelLabel = '',
		objectionOptions = [],
		params = {},
		areaOptions = [],
		areaSelectOptions = [],
		worthDefaults = {},
		eventSelections = [],
		contactsOptions = {},
		companyId,
		activityId = null,
		initialComment = '',
		initialStatus = null,
		initialStatusReason = null,
		initialStatusDate = null,
		initialPayloadItems = [],
		onAction: onActionProp,
		onSuccess,
		cancelHandler = () => {},
		...rest
	} = props;

	const isEditMode = Boolean(activityId);
	const defaultStatus =
		statusProps.defaultValue || NO_CHANGE_STATUS_OPTION;

	const [action, setAction] = useState(defaultAction);
	const [comment, setComment] = useState(initialComment);
	const [status, setStatus] = useState(initialStatus || defaultStatus);
	const [statusReason, setStatusReason] = useState(initialStatusReason);
	const [statusDate, setStatusDate] = useState(() =>
		initialStatusDate ? dayjs(initialStatusDate) : null,
	);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState(null);
	const [commentError, setCommentError] = useState(null);
	const { groups, payloadItems, addDataToGroup, resetGroups } =
		useEventFormGroups(initialPayloadItems);

	const isPostponed = status?.code === COMPANY_STATUS_CODE.POSTPONED;
	const isLoss =
		status?.code === COMPANY_STATUS_CODE.COMPLETED_WITH_LOSS;
	const withStatusReason = isPostponed || isLoss;
	const withStatusDate = isPostponed;

	const worthValuationPre = useMemo(() => {
		const table = mapEventSelectionsToTableValuation(eventSelections);

		return table ? <TableValuation {...table} /> : undefined;
	}, [eventSelections]);

	const stagedParamsValues = useMemo(() => {
		const staged = [...payloadItems]
			.reverse()
			.find((item) => item.type === 'params');

		if (!staged?.data) return undefined;

		const emails = (staged.data.emails ?? []).filter(Boolean);
		const sites = (staged.data.sites ?? []).filter(Boolean);

		return {
			date: staged.data.date ?? null,
			emails: emails.length ? emails : [''],
			sites: sites.length ? sites : [''],
		};
	}, [payloadItems]);

	const modalConfig = useMemo(
		() => [
			[
				ACTION_CODE.params,
				DefaultFormCAreaParams,
				{ initialData: params, values: stagedParamsValues },
			],
			[ACTION_CODE.changeName, DefaultFormCAreaName],
			[
				ACTION_CODE.connected,
				DefaultFormCConnectedAreas,
				{
					options: areaSelectOptions,
					excludeIds: companyId ? [companyId] : [],
				},
			],
			[ACTION_CODE.addObjection, DefaultFormCAddObjection],
			[ACTION_CODE.addCommonNumber, DefaultFormCCommonNumber],
			[ACTION_CODE.addPersonalContact, DefaultFormCPersonNumber],
			[
				ACTION_CODE.addArea,
				DefaultFormCConnectedAreas,
				{
					options: areaSelectOptions,
					excludeIds: companyId ? [companyId] : [],
				},
			],
			[ACTION_CODE.addContract, DefaultFormCDocUpload],
			[
				ACTION_CODE.addDoc,
				DefaultFormCDocUpload,
				{ title: 'Добавить документ', sign: false },
			],
			[
				ACTION_CODE.removeObjection,
				DefaultFormCRemoveObjection,
				{ options: objectionOptions },
			],
			[
				ACTION_CODE.editContact,
				DefaultFormCChangeContact,
				{ contacts: contactsOptions },
			],
			[
				ACTION_CODE.removeArea,
				DefaultFormCRemoveArea,
				{ options: areaOptions },
			],
			[ACTION_CODE.calls, CallsPickerForm],
			[
				ACTION_CODE.worth,
				DefaultFormCWorth,
				{
					initialData: mapWorthToFormValues(worthDefaults),
					options: buildWorthFormOptions(worthDefaults),
					pre: worthValuationPre,
				},
			],
			[ACTION_CODE.meeting, DefaultFormCMeeting],
			[ACTION_CODE.messaging, DefaultFormCMessaging],
		],
		[
			areaOptions,
			areaSelectOptions,
			companyId,
			contactsOptions,
			objectionOptions,
			params,
			stagedParamsValues,
			worthDefaults,
			worthValuationPre,
		],
	);

	const modals = useMemo(
		() =>
			modalConfig.map(([code, Component, extraProps]) => ({
				code,
				Modal: extraProps
					? (modalProps) => (
						<Component {...extraProps} {...modalProps} />
					)
					: Component,
			})),
		[modalConfig],
	);

	const handleAction = (actionCode) => {
		setAction(actionCode);
		onActionProp?.(actionCode);
	};

	const handleModalSubmit = (_value, modalInitialData) => {
		const config = MODAL_ACTION_MAP[action];

		if (config) {
			let data = _value;
			const initialData =
				config.type === 'params'
					? (modalInitialData ?? params)
					: modalInitialData;

			if (config.type === 'companyGroup' && config.action === 'unlink') {
				data = {
					areas: (_value.areas || []).map((id) => {
						const option = areaOptions.find(
							(entry) => String(entry.value) === String(id),
						);

						return {
							label: option?.label ?? String(id),
							value: String(id),
						};
					}),
				};
			}

			if (config.type === 'companyGroup' && config.action === 'link') {
				data = {
					areas: (_value.areas || []).filter((area) => area?.value),
				};
			}

			if (
				config.type === 'contact' &&
				config.action === 'change' &&
				!mapContactChangePreview(data)
			) {
				return;
			}

			addDataToGroup(config.type, config.action, data, initialData);
		}

		setAction(null);
	};

	const handleSubmit = useCallback(async () => {
		if (!comment.trim()) {
			setCommentError('Укажите комментарий');
			setSubmitError('Укажите комментарий');
			return;
		}

		if (!companyId) {
			setSubmitError('Не удалось определить компанию');
			return;
		}

		if (isPostponed && !statusDate) {
			setSubmitError('Укажите дату отложения');
			return;
		}

		if (withStatusReason && !statusReason?.value) {
			setSubmitError('Укажите причину');
			return;
		}

		setCommentError(null);
		setSubmitError(null);
		setIsSubmitting(true);

		try {
			const input = mapActivityPayload({
				companyId,
				comment,
				status,
				statusLossReason: isLoss ? statusReason : null,
				statusPostponedReason: isPostponed ? statusReason : null,
				statusPostponedUntil: isPostponed ? statusDate : null,
				payloadItems,
				worthDefaults,
			});

			if (isEditMode) {
				await updateActivity(activityId, input);
			} else {
				await createActivity(input);
			}

			resetGroups();
			setComment('');
			setCommentError(null);
			setStatus(defaultStatus);
			setStatusReason(null);
			setStatusDate(null);
			onSuccess?.();
		} catch (error) {
			setSubmitError(
				error?.message
					? error
					: isEditMode
						? 'Не удалось изменить событие'
						: 'Не удалось создать событие',
			);
		} finally {
			setIsSubmitting(false);
		}
	}, [
		activityId,
		comment,
		companyId,
		defaultStatus,
		isEditMode,
		isLoss,
		isPostponed,
		onSuccess,
		payloadItems,
		resetGroups,
		status,
		statusDate,
		statusReason,
		withStatusReason,
		worthDefaults,
	]);

	return (
		<>
			<EventForm
				groups={groups}
				comment={{
					...commentProps,
					value: comment,
					error: commentError,
					onChange: (event) => {
						setComment(event.target.value);
						if (commentError) setCommentError(null);
					},
				}}
				status={{
					...statusProps,
					value: status,
					onChange: (_event, value) => {
						const next = value || defaultStatus;
						setStatus(next);

						const nextIsPostponed =
							next?.code === COMPANY_STATUS_CODE.POSTPONED;
						const nextIsLoss =
							next?.code ===
							COMPANY_STATUS_CODE.COMPLETED_WITH_LOSS;

						if (nextIsPostponed || nextIsLoss) {
							setStatusReason(
								(current) => current || STATUS_REASON_OPTIONS[0],
							);
						} else {
							setStatusReason(null);
						}

						if (!nextIsPostponed) {
							setStatusDate(null);
						}
					},
				}}
				withStatusDate={withStatusDate}
				withStatusReason={withStatusReason}
				statusDate={{
					placeholder: 'Выберите дату',
					minDate: dayjs().startOf('day'),
					value: statusDate,
					onChange: (value) => setStatusDate(value),
				}}
				statusReason={{
					label: 'Причина',
					placeholder: 'Выберите причину',
					selectOnly: true,
					disableClearable: true,
					options: STATUS_REASON_OPTIONS,
					value: statusReason,
					onChange: (_event, value) => setStatusReason(value),
				}}
				actions={actions}
				submitLabel={
					submitLabel ||
					(isEditMode ? 'Сохранить изменения' : 'Создать событие')
				}
				cancelLabel={cancelLabel}
				onAction={handleAction}
				onSubmit={handleSubmit}
				isSubmitting={isSubmitting}
				submitError={submitError}
				cancelHandler={cancelHandler}
				{...rest}
			/>
			{modals.map(({ code, Modal }, i) => {
				return (
					<RightDrawer
						key={i}
						open={action === code}
						onCloseDrawer={() => {
							setAction(null);
						}}
					>
						{action === code ? (
							<Modal
								key={code}
								onCancel={() => {
									setAction(null);
								}}
								fullHeight
								onSubmit={handleModalSubmit}
								{...rest}
							/>
						) : null}
					</RightDrawer>
				);
			})}
		</>
	);
};

EventFormCWithModals.propTypes = {
	defaultAction: PropTypes.string,
	comment: PropTypes.object,
	status: PropTypes.object,
	actions: PropTypes.object,
	submitLabel: PropTypes.string,
	cancelLabel: PropTypes.string,
	objectionOptions: PropTypes.array,
	areaOptions: PropTypes.array,
	areaSelectOptions: PropTypes.array,
	contactsOptions: PropTypes.array,
	worthDefaults: PropTypes.object,
	eventSelections: PropTypes.array,
	companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	activityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	initialComment: PropTypes.string,
	initialStatus: PropTypes.object,
	initialStatusReason: PropTypes.object,
	initialStatusDate: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.object,
	]),
	initialPayloadItems: PropTypes.array,
	params: PropTypes.object,
	onAction: PropTypes.func,
	onSuccess: PropTypes.func,
	cancelHandler: PropTypes.func,
};

export default EventFormCWithModals;
